import { useCallback, useMemo } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom-v5-compat";
import { CARS_BOOK_PATH } from "@apac/components/cars";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { CoreUiComponents, FlightShopStep } from "@b2bportal/core-types";
import { HOTELS_BOOK_PATH, useDeviceType } from "@b2bportal/core-utilities";
import { CheckoutQueryParams, ParentState } from "@checkout/types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ProductType } from "@hopper-b2b/types";
import {
  PointsBalanceModalContent,
  BookingSummary,
  FlightScore,
  MobileNavigation,
} from "@hsbc/portal";
import { usePointsBalance } from "@hsbc/rewards";
import {
  CARS_PATH,
  TRIPS_PATH,
  FLIGHTS_PATH,
  HOTELS_PATH,
  FLIGHTS_BOOK_PATH,
} from "@hsbc/utilities";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
  type PageHeaderComponentProps,
} from "@tenant/ui";
import styles from "./styles.module.scss";

export interface HsbcPageHeaderProps extends PageHeaderComponentProps {
  onBackClick?: () => void;
  title?: string;
  hideBackButton?: boolean;
  hideSummary?: boolean;
  hideSubHeader?: boolean;
  hidePoints?: boolean;
}

export const PageHeader = ({
  onBackClick,
  title,
  hideBackButton = false,
  hideSummary = false,
  hideSubHeader = false,
  hidePoints = false,
}: HsbcPageHeaderProps) => {
  const { t, brand, formatRewardsCurrency } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const [block, cn] = useModuleBEM(styles, CoreUiComponents.PageHeader);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { rewardsPoints, isLoading } = usePointsBalance();

  const [searchParams] = useSearchParams();
  const onCheckoutReviewPage =
    searchParams.get(CheckoutQueryParams.checkoutState) === ParentState.review;

  const showFlightScore = useCallback(
    (pathname: string) =>
      pathname.endsWith(FlightShopStep.departure) ||
      pathname.endsWith(FlightShopStep.return),
    []
  );

  // Show subheader for shopping and booking flow only
  const showSubheader = useCallback(
    (pathname: string, hideSubHeader: boolean) =>
      !hideSubHeader &&
      (pathname.startsWith(FLIGHTS_PATH) ||
        pathname.startsWith(HOTELS_PATH) ||
        pathname.startsWith(CARS_PATH) ||
        pathname.startsWith(TRIPS_PATH + "/")),
    []
  );

  const productType = useMemo(() => {
    if (pathname.startsWith(FLIGHTS_PATH)) {
      return ProductType.FLIGHTS;
    }
    if (pathname.startsWith(HOTELS_PATH)) {
      return ProductType.LODGING;
    }
    return ProductType.CARS;
  }, [pathname]);

  const showSummary = useCallback(
    (pathname: string, hideSummary: boolean) =>
      !hideSummary &&
      !onCheckoutReviewPage &&
      (pathname.startsWith(FLIGHTS_BOOK_PATH) ||
        pathname.startsWith(HOTELS_BOOK_PATH) ||
        pathname.startsWith(CARS_BOOK_PATH)),
    [onCheckoutReviewPage]
  );

  return (
    <div className={block}>
      <div className={cn("top-section")}>
        {!isDesktopAndUp && <MobileNavigation className={cn("menu")} />}
        <h1 className={cn("portal-name")}>
          <Link to="/">{brand.portalName}</Link>
        </h1>
        <div className={cn("subtitle-line")}>
          <span className={cn("subtitle")}> {t("portalSubname")}</span>
          {!hidePoints && (
            <Dialog>
              <DialogTrigger asChild>
                <button className={cn("balance")}>
                  {rewardsPoints?.value != null && !isLoading
                    ? formatRewardsCurrency(rewardsPoints)
                    : ""}
                </button>
              </DialogTrigger>
              <DialogContent title={t("rewardsPointsModal.dialogTitle")}>
                <PointsBalanceModalContent />

                <DialogClose className={cn("close-button")}>
                  {t("close.button")}
                </DialogClose>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {showSubheader(pathname, hideSubHeader) && (
        <div className={cn("bottom-section")}>
          <div>
            {!hideBackButton && (
              <button
                onClick={() => (onBackClick ? onBackClick() : navigate(-1))}
                className={cn("back-button")}
                aria-label={t("back")}
              >
                <Icon iconName={IconNameEnum.backIcon} />
                {t("back")}
              </button>
            )}
          </div>
          <div className={cn("title")}>{title}</div>

          <div className={cn("breakdown")}>
            {showSummary(pathname, hideSummary) &&
              (showFlightScore(pathname) ? (
                <FlightScore />
              ) : (
                <BookingSummary productType={productType} />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
